import React, { ReactNode, useState } from 'react'

import { AxiosError } from 'axios'
import {
  Button as MuiButton,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import toast from 'react-hot-toast'
import styled from 'styled-components'
import * as yup from 'yup'

import { skuit } from 'src/services/skuit'
import { FreightShipment, Truckload } from 'src/api/models'
import { MapSelectField, QueryMultiSelectField, QuerySelectField, TextField, useForm } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FreightShipmentCompact } from 'src/modules/shared/components/cards/compact'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))


interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

interface TruckloadProps {
  client?: string
  facility: string
  carrier: string
  tracking_number: string // eslint-disable-line camelcase
  bill_of_lading_number: string // eslint-disable-line camelcase
  trailer_number: string // eslint-disable-line camelcase
  status: string
  shipments: string[]
  attachments?: AttachmentProp[]
}

const truckloadSchema = yup.object().shape({
  client: yup.string().required().label('Client'),
  facility: yup.string().required().label('Facility'),
  carrier: yup.string().label('Carrier'),
  tracking_number: yup.string().label('Tracking Number'),
  bill_of_lading_number: yup.string().label('Bill of Lading Number'),
  trailer_number: yup.string().label('Trailer Number'),
  status: yup.string().label('Status').required(),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  truckload?: Truckload
  shipment?: FreightShipment
}

export const TruckloadDialog = (props: Props) => {
  const classes = useStyles()
  const { open, handleClose, onSave, update = false, truckload, shipment } = props

  const [saving, setSaving] = useState(false)
  const [clientGuid, setClientGuid] = useState<string | null>(truckload?.client?.guid || shipment?.client?.guid || null)

  const clientPaginator = useQueryPaginator('client-list', skuit.sc.clients.list, {
    params: { search: truckload?.client?.name },
  })
  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const carrierPaginator = useQueryPaginator('carreir-list', skuit.sc.carriers.list)
  const shipmentPaginator = useQueryPaginator(
    'shipment-list',
    skuit.sc.freight.shipments.list,
    { params: { facilities: truckload?.facility?.guid, open: true, no_truckload: true, clients: clientGuid || 'none' } }
  )
  const truckloadShipmentsPaginator = useQueryPaginator(
    'truckload-shipment-list',
    skuit.sc.freight.shipments.list,
    { params: { page: 'all', truckloads: truckload?.guid || 'none' } }
  )
  const tagPaginator = useQueryPaginator(
    'tag-list',
    skuit.sc.attachments.tags.list,
    {},
  )

  const { Form, Field, FieldList, data: formData } = useForm(truckloadSchema)

  const onSubmit = (data: TruckloadProps) => {
    setSaving(true)

    const attachments = data.attachments
    delete data.attachments

    let func = skuit.sc.freight.truckloads.create
    let action = 'creat'
    if (update) {
      func = (data: TruckloadProps) => skuit.sc.freight.truckloads.patch(truckload?.guid || '', data)
      delete data.client
      action = 'updat'
    }

    func(data)
      .then((newTruckload: Truckload) => {
        if (attachments?.length) {
          skuit.sc.freight.truckloads.attachments
            .create(newTruckload.guid as string, { attachments })
            .then(() => {
              handleClose()
              if (onSave) {
                onSave()
              }
              toast.success(() => (
                <Notification
                  message={`Successfully ${action}ed Order: ${newTruckload.name}`}
                  newObject={newTruckload}
                />
              ))
              toast.success(() => (
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              ))
            })
            .catch((attachmentError: AxiosError) => {
              handleErrorMessage(attachmentError, action, 'Attachments')
              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          if (onSave) {
            onSave()
          }
          toast.success(() => (
            <Notification
              message={`Successfully ${action}ed Truckload: ${newTruckload.name}`}
              newObject={newTruckload}
            />
          ))
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Truckload')
        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{truckload ? 'Update' : 'Create'} Truckload</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <QuerySelectField
                name="client"
                label="Client"
                disabled={update}
                paginator={clientPaginator}
                defaultValue={truckload?.client || shipment?.client}
                onSelected={(o) => setClientGuid(o.guid)}
              />
            </Grid>
            <Grid xs={12}>
              <QuerySelectField
                name="facility"
                label="Facility"
                disabled={!clientGuid}
                paginator={facilityPaginator}
                defaultValue={truckload?.facility || shipment?.facility}
              />
            </Grid>
            <Grid xs={12}>
              <QuerySelectField
                name="carrier"
                label="Carrier"
                disabled={!clientGuid}
                paginator={carrierPaginator}
                defaultValue={truckload?.carrier || shipment?.carrier}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                name="tracking_number"
                label="Tracking Number"
                disabled={!clientGuid}
                defaultValue={truckload?.trackingNumber || shipment?.trackingNumber}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                name="bill_of_lading_number"
                label="Bill of Lading Number"
                disabled={!clientGuid}
                defaultValue={truckload?.billOfLadingNumber || shipment?.bol}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                name="trailer_number"
                label="Trailer Number"
                disabled={!clientGuid}
                defaultValue={truckload?.trailerNumber}
              />
            </Grid>
            <Grid item xs={6}>
              <MapSelectField
                name="status"
                label="Status"
                options={Truckload.Status}
                defaultValue={truckload?.status || 'empty'}
              />
            </Grid>
            <Grid xs={12}>
              <QueryMultiSelectField
                name="shipments"
                label="Shipments"
                disabled={!clientGuid}
                paginator={shipmentPaginator}
                defaultValue={truckloadShipmentsPaginator.data.results.length ? truckloadShipmentsPaginator.data.results : (shipment ? [shipment] : undefined)}
                getOptionLabel={(obj) => <FreightShipmentCompact obj={obj} />}
                renderSelected={FreightShipmentCompact}
              />
            </Grid>
          </Grid>
          <>
            <Typography variant="h6" mt={6} mb={3}>
              Attachments
              </Typography>
            <FieldList
              name="attachments"
              startCount={0}
              renderHeader={({ name, value, setValue }) =>
                truckload?.status === 'canceled' ? null : (
                  <DropzoneAreaBase
                    dropzoneText="Drag and drop or click to add files"
                    fileObjects={[]}
                    onAdd={(files) => {
                      const fileData = files.map((fileObj) => {
                        return { file: fileObj.file, tags: [] }
                      })
                      const newValue = (value || []).concat(fileData)
                      setValue(name, newValue)
                    }}
                    filesLimit={10}
                    maxFileSize={75000000}
                    showPreviewsInDropzone={false}
                  />
                )
              }
              renderList={ImageListWrapper}
              renderField={({ name: itemName }) => (
                <Grid item xs={3} key={itemName}>
                  <Card>
                    <CardContent>
                      <Field
                        name="file"
                        renderField={({ value, remove, error }) => {
                          if (!value) {
                            return null
                          }
                          return (
                            <>
                              <FilePreview file={value} onDelete={() => remove(itemName)} />
                              {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                            </>
                          )
                        }}
                      />
                      <Field
                        name="url"
                        renderField={({ value, remove, error }) => {
                          if (!value) {
                            return null
                          }
                          return (
                            <>
                              <UrlPreview
                                url={value}
                                allowDownload={false}
                                onDelete={() => remove(itemName)}
                              />
                              {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                            </>
                          )
                        }}
                      />
                      <Field
                        name="tags"
                        getSubmitValue={(value) =>
                          value && value.map((tag: { slug: string }) => tag.slug)
                        }
                        renderField={({ name, value, setValue, error }) => (
                          <Autocomplete
                            id={name}
                            freeSolo
                            options={tagPaginator.data.results}
                            getOptionLabel={(tag) => tag.name}
                            getOptionSelected={(option, value) => option.guid === value?.guid}
                            onChange={(_: any, newValue: any | null) => setValue(name, newValue)}
                            value={value}
                            renderInput={(params) => (
                              <MuiTextField
                                {...params}
                                error={!!error}
                                helperText={error}
                                label="Tags"
                              />
                            )}
                          />
                        )}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            />
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving} autoFocus>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
